import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_SERVER_ADDRESS } from '../../environments/environment';
import { TranslateConfigService } from './translate-config.service';
import { SolicitudReserva } from '../_models/solicitud-reserva';
import { PlazaReservada } from '../_models/plaza-reservada.model';

export interface PrecioTarifa {
  idFare: string | number;
  price: number;
  tarifa: { nom_web: string; es_vendible: boolean } | string;
}

export interface TarifaConNombre extends PrecioTarifa {
  nombreTraducido: string;
}

export interface CrearReservaDto {
  usuario_id: number;
  importe: number;
  origen: string;
  plazas_reservadas: Omit<PlazaReservada, 'id' | 'solicitud_reserva_id' | 'created' | 'validado' | 'validado_tarjeta_tmg' | 'validado_tarjeta_tmg_fecha' | 'validado_conductor' | 'validado_conductor_fecha' | 'anulado' | 'anulado_fecha' | 'estado' | 'qr_code' | 'es_fallida' | 'motivo_fallida' | 'fecha_fallida' | 'perdonada' | 'fecha_perdon' | 'motivo_perdon' | 'perdonada_por' | 'usuario'>[];
}

export interface AnularReservaDto {
  motivo: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReservaService {

  private apiUrl = `${API_SERVER_ADDRESS}`;

  constructor(
    private http: HttpClient,
    private translateConfigService: TranslateConfigService
  ) { }

  /**
   * Validar si una expedición permite reservas
   * @param expedicion - Datos de la expedición
   */
  isExpedicionReservable(expedicion: any): boolean {
    return expedicion?.reserva_info?.permite_reservas === true;
  }

  /**
   * Obtener tarifas reservables de una expedición (Regular y TMG)
   * @param expedicion - Datos de la expedición
   */
  getTarifasReservables(expedicion: { precios?: PrecioTarifa[] }): PrecioTarifa[] {
    if (!expedicion.precios) {
      return [];
    }

    return expedicion.precios.filter((precio: PrecioTarifa) => {
      const nomWeb = typeof precio.tarifa === 'object' ? precio.tarifa?.nom_web?.toLowerCase() : null;
      return nomWeb === 'normal' || nomWeb === 'tmg';
    });
  }

  /**
   * Obtener tarifa por defecto para reservas (TMG si disponible, sino Regular)
   * @param expedicion - Datos de la expedición
   */
  private getTarifaReservaDefecto(expedicion: { precios?: PrecioTarifa[] }): PrecioTarifa | null {
    const tarifasReservables = this.getTarifasReservables(expedicion);

    // Priorizar TMG
    const tarifaTMG = tarifasReservables.find(precio => {
      const nomWeb = typeof precio.tarifa === 'object' ? precio.tarifa?.nom_web?.toLowerCase() : null;
      return nomWeb === 'tmg';
    });
    if (tarifaTMG && tarifaTMG.price) {
      return tarifaTMG;
    }

    // Fallback a Normal
    const tarifaNormal = tarifasReservables.find(precio => {
      const nomWeb = typeof precio.tarifa === 'object' ? precio.tarifa?.nom_web?.toLowerCase() : null;
      return nomWeb === 'normal';
    });
    if (tarifaNormal && tarifaNormal.price) {
      return tarifaNormal;
    }

    // Último fallback: cualquier precio disponible
    if (expedicion?.precios && expedicion.precios.length > 0) {
      const primerPrecio = expedicion.precios[0];
      if (primerPrecio && primerPrecio.price) {
        return primerPrecio;
      }
    }

    return null;
  }

  /**
   * Obtener precio en centavos para una expedición
   * @param expedicion - Datos de la expedición
   */
  getPrecioReserva(expedicion: { precios?: PrecioTarifa[] }): number {
    const tarifa = this.getTarifaReservaDefecto(expedicion);
    if (tarifa && tarifa.price) {
      return Math.round(tarifa.price * 100); // Convertir a centavos
    }
    return 0;
  }

  /**
   * Obtener ID de tarifa para una expedición
   * @param expedicion - Datos de la expedición
   */
  getTarifaIdReserva(expedicion: { precios?: PrecioTarifa[] }): string | number {
    const tarifa = this.getTarifaReservaDefecto(expedicion);
    if (tarifa && tarifa.idFare) {
      return tarifa.idFare;
    }
    return 100;
  }

  /**
   * Obtener nombre traducido de tarifa según su ID
   * @param tarifaId - ID de la tarifa
   */
  private getNombreTarifaTraducido(tarifa: PrecioTarifa['tarifa']): string {
    const nomWeb = typeof tarifa === 'object' ? tarifa?.nom_web?.toLowerCase() : null;
    if (nomWeb === 'normal') {
      return this.translateConfigService.getKey('RESERVAR.precio_regular');
    }
    if (nomWeb === 'tmg') {
      return this.translateConfigService.getKey('RESERVAR.precio_tmg');
    }
    return typeof tarifa === 'object' ? tarifa?.nom_web || 'Tarifa' : 'Tarifa';
  }

  getTarifaReservaConNombre(expedicion: { precios?: PrecioTarifa[] }): TarifaConNombre | null {
    const tarifa = this.getTarifaReservaDefecto(expedicion);
    if (tarifa) {
      return {
        ...tarifa,
        nombreTraducido: this.getNombreTarifaTraducido(tarifa.tarifa)
      };
    }
    return null;
  }

  getTarifasReservablesConNombre(expedicion: { precios?: PrecioTarifa[] }): TarifaConNombre[] {
    const tarifas = this.getTarifasReservables(expedicion);
    return tarifas.map(tarifa => ({
      ...tarifa,
      nombreTraducido: this.getNombreTarifaTraducido(tarifa.tarifa)
    }));
  }

  /**
   * Listar reservas del usuario
   * @param q_origen - Filtro por origen
   * @param q - Búsqueda general
   * @returns Observable con las reservas envueltas en { reservas: SolicitudReserva[] }
   */
  listarReservas(q_origen?: string, q?: string): Observable<{ reservas: SolicitudReserva[] }> {
    let params = new HttpParams();
    return this.http.get<{ reservas: SolicitudReserva[] }>(`${this.apiUrl}/v1/reservas`, { params });
  }

  /**
   * Listar reservas activas del usuario
   * @param q_origen - Filtro por origen
   * @param q - Búsqueda general
   * @returns Observable con las reservas envueltas en { reservas: SolicitudReserva[] }
   */
  listarReservasActivas(q_origen?: string, q?: string): Observable<{ reservas: SolicitudReserva[] }> {
    let params = new HttpParams();
    params = params.set('finders', 'conTodasPlazasActivas');

    return this.http.get<{ reservas: SolicitudReserva[] }>(`${this.apiUrl}/v1/reservas`, { params });
  }


  /**
   * Crear nueva reserva
   * @param reservaData - Datos de la reserva
   * @returns Observable con la reserva creada envuelta en { reserva: SolicitudReserva }
   */
  crearReserva(reservaData: CrearReservaDto): Observable<{ reserva: SolicitudReserva }> {
    const data = { ...reservaData, origen: 'Reserva app' };
    return this.http.post<{ reserva: SolicitudReserva }>(`${this.apiUrl}/v1/reservas`, data);
  }

  /**
   * Obtener reserva específica
   * @param reservaId - ID de la reserva
   * @returns Observable con la reserva envuelta en { reserva: SolicitudReserva }
   */
  obtenerReserva(reservaId: number): Observable<{ reserva: SolicitudReserva }> {
    return this.http.get<{ reserva: SolicitudReserva }>(`${this.apiUrl}/v1/reservas/${reservaId}`);
  }

  /**
   * Verificar si una solicitud de reserva puede ser anulada
   * Una reserva solo puede anularse si TODAS sus plazas están en estado 'activa'
   * @param solicitud - Solicitud de reserva a verificar
   * @returns true si puede anularse, false en caso contrario
   */
  puedeAnularReserva(solicitud: SolicitudReserva): boolean {
    // Si no hay plazas reservadas, no se puede anular
    if (!solicitud.plazas_reservadas || solicitud.plazas_reservadas.length === 0) {
      return false;
    }

    // Si la solicitud ya está anulada, no se puede volver a anular
    if (solicitud.anulado) {
      return false;
    }

    // Verificar que TODAS las plazas estén en estado 'activa'
    return solicitud.plazas_reservadas.every(plaza => plaza.estado === 'activa');
  }

  /**
   * Anular reserva
   * @param reservaId - ID de la reserva
   * @param motivo - Motivo de la anulación
   * @returns Observable con la reserva anulada
   */
  anularReserva(reservaId: number, motivo: string): Observable<any> {
    const data: AnularReservaDto = { motivo };
    return this.http.patch(`${this.apiUrl}/v1/reservas/${reservaId}/anular`, data);
  }
}